// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertCircle from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import {
    hasMeaningfulMinimumOrderQuantity,
    resolveProductLineMinimumOrderQuantity,
} from "@store/Framework/Common/Utilities/productLineUtilities";
import { ProductLineComponentModel } from "@store/Library/Components/ProductLine/ProductLine";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC } from "react";
import { css } from "styled-components";

export interface ProductLineMinimumOrderQuantityPresentationProps
    extends ExpandStyles<ProductLineMinimumOrderQuantityStyles> {}

export interface ProductLineMinimumOrderQuantityProps extends ProductLineMinimumOrderQuantityPresentationProps {
    /** Takes precedence over "productLine" for consumers that already resolved the value. */
    minimumOrderQuantity?: number;
    productLine?: ProductLineComponentModel;
}

export interface ProductLineMinimumOrderQuantityStyles {
    wrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const productLineMinimumOrderQuantityStyles: ProductLineMinimumOrderQuantityStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(6))}
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: ${pxToRem(4)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    label: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    tooltip: {
        iconProps: {
            size: 16,
            src: AlertCircle,
        },
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                margin: 0;
            `,
        },
    },
};

const ProductLineMinimumOrderQuantity: FC<ProductLineMinimumOrderQuantityProps> = ({
    extendedStyles,
    minimumOrderQuantity,
    overridedStyles,
    productLine,
}) => {
    const styles = useExpandProps(productLineMinimumOrderQuantityStyles, extendedStyles, overridedStyles);

    const resolvedMinimumOrderQuantity = minimumOrderQuantity ?? resolveProductLineMinimumOrderQuantity(productLine);

    if (!hasMeaningfulMinimumOrderQuantity(resolvedMinimumOrderQuantity)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productLine_minimumOrderQuantity">
            <Typography {...styles.label}>{translate("{0}:", translate("MOQ"))}</Typography>
            <Typography {...styles.value}>{translate("{0} units", String(resolvedMinimumOrderQuantity))}</Typography>
            <Tooltip {...styles.tooltip} text={translate("Minimum Order Quantity")} />
        </StyledWrapper>
    );
};

export default ProductLineMinimumOrderQuantity;
